import { Card } from '../Models/Card'
import { appState, cardBackImageSource } from './state'
import { getCardPosition } from './cardPositions'
import { isLeftHanded, tableauCardStep } from './allXYCoordinates'

// Border radius scales with card size (~13% of card height — Apple HIG card-element range).
// Canvas roundRect uses circular arcs; Apple uses continuous corners (squircle), but the
// visual difference is subtle at these radii.
export function getBorderRadius(): number {
    const cardH = appState.cardHeight || 100
    return Math.max(6, Math.min(30, Math.floor(cardH * 0.13)))
}

export function drawOutline(
    startX: number,
    startY: number,
    width: number,
    height: number,
    ctx: CanvasRenderingContext2D
) {
    // Minimal placeholder — filled rounded rect, no border, low visual weight
    ctx.beginPath()
    ctx.roundRect(startX, startY, width, height, getBorderRadius() * 0.6)
    ctx.fillStyle = appState.currentTheme.placeholderColor
    ctx.fill()
}

function drawCardBack(
    startX: number,
    startY: number,
    width: number,
    height: number,
    ctx: CanvasRenderingContext2D
) {
    const radius = getBorderRadius()

    // Draw rounded rectangle background — no shadowBlur (expensive on iOS Safari)
    ctx.beginPath()
    ctx.roundRect(startX, startY, width, height, radius)
    ctx.fillStyle = appState.currentTheme.cardBackColor
    ctx.fill()

    // Draw card back image only if pattern mode is enabled
    if (
        appState.settings.cardBackDesign === 'pattern' &&
        cardBackImageSource &&
        (cardBackImageSource as HTMLImageElement).complete
    ) {
        ctx.save()
        ctx.beginPath()
        ctx.roundRect(startX, startY, width, height, radius)
        ctx.clip()
        ctx.drawImage(cardBackImageSource, startX, startY, width, height)
        ctx.restore()
    }

    // Border stroke for edge definition (V3: face-down cards were just solid blobs)
    ctx.beginPath()
    ctx.roundRect(startX, startY, width, height, radius)
    ctx.lineWidth = Math.max(1.5, width * 0.03)
    ctx.strokeStyle = appState.currentTheme.cardBorderColor
    ctx.stroke()
}

// ─── Canvas-rendered card faces (V5) ────────────────────────────────────
// White paper + colored ink. Top/fully-visible cards get a large centered
// suit (~65% card height) drawn with vector canvas paths; partially-hidden
// cards in cascades only show the corner value (prevents the suit from
// peeking out of the exposed sliver). Corner mirrors with handedness so
// the value is always on the exposed cascade edge.

// ─── Vector suit paths ───────────────────────────────────────────────────
// Canvas-drawn suit symbols for pixel-perfect rendering. Each function
// draws the suit centered at (cx, cy) within a `size` × `size` box.
// Style: V4 Sharp Point — sharp heart tip, subtle diamond curve, clean proportions.

export function drawHeart(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number) {
    const h = size * 0.46
    ctx.save()
    ctx.translate(cx, cy)
    ctx.beginPath()
    ctx.moveTo(0, h * 0.95)
    ctx.bezierCurveTo(-h * 0.08, h * 0.7, -h * 0.85, h * 0.15, -h * 0.85, -h * 0.18)
    ctx.bezierCurveTo(-h * 0.85, -h * 0.7, -h * 0.42, -h * 0.9, 0, -h * 0.52)
    ctx.bezierCurveTo(h * 0.42, -h * 0.9, h * 0.85, -h * 0.7, h * 0.85, -h * 0.18)
    ctx.bezierCurveTo(h * 0.85, h * 0.15, h * 0.08, h * 0.7, 0, h * 0.95)
    ctx.closePath()
    ctx.fill()
    ctx.restore()
}

function drawDiamond(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number) {
    const hw = size * 0.32,
        hh = size * 0.48
    ctx.beginPath()
    ctx.moveTo(cx, cy - hh)
    ctx.bezierCurveTo(cx + hw * 0.15, cy - hh * 0.55, cx + hw * 0.7, cy - hh * 0.12, cx + hw, cy)
    ctx.bezierCurveTo(cx + hw * 0.7, cy + hh * 0.12, cx + hw * 0.15, cy + hh * 0.55, cx, cy + hh)
    ctx.bezierCurveTo(cx - hw * 0.15, cy + hh * 0.55, cx - hw * 0.7, cy + hh * 0.12, cx - hw, cy)
    ctx.bezierCurveTo(cx - hw * 0.7, cy - hh * 0.12, cx - hw * 0.15, cy - hh * 0.55, cx, cy - hh)
    ctx.closePath()
    ctx.fill()
}

function drawSpade(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number) {
    const h = size * 0.46
    ctx.save()
    ctx.translate(cx, cy)
    ctx.beginPath()
    ctx.moveTo(0, -h * 0.95)
    ctx.bezierCurveTo(-h * 0.08, -h * 0.6, -h * 0.78, -h * 0.15, -h * 0.78, h * 0.18)
    ctx.bezierCurveTo(-h * 0.78, h * 0.52, -h * 0.35, h * 0.56, -h * 0.05, h * 0.35)
    ctx.quadraticCurveTo(-h * 0.15, h * 0.65, -h * 0.25, h * 0.88)
    ctx.lineTo(h * 0.25, h * 0.88)
    ctx.quadraticCurveTo(h * 0.15, h * 0.65, h * 0.05, h * 0.35)
    ctx.bezierCurveTo(h * 0.35, h * 0.56, h * 0.78, h * 0.52, h * 0.78, h * 0.18)
    ctx.bezierCurveTo(h * 0.78, -h * 0.15, h * 0.08, -h * 0.6, 0, -h * 0.95)
    ctx.closePath()
    ctx.fill()
    ctx.restore()
}

function drawClub(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number) {
    const h = size * 0.46,
        r = h * 0.3
    ctx.save()
    ctx.translate(cx, cy)
    // Fill center triangle to eliminate gap between lobes
    ctx.beginPath()
    ctx.moveTo(0, -h * 0.32)
    ctx.lineTo(-h * 0.3, h * 0.1)
    ctx.lineTo(h * 0.3, h * 0.1)
    ctx.closePath()
    ctx.fill()
    // Each lobe drawn separately to avoid canvas connecting arcs with lines
    ctx.beginPath()
    ctx.arc(0, -h * 0.32, r, 0, Math.PI * 2)
    ctx.fill()
    ctx.beginPath()
    ctx.arc(-h * 0.3, h * 0.1, r, 0, Math.PI * 2)
    ctx.fill()
    ctx.beginPath()
    ctx.arc(h * 0.3, h * 0.1, r, 0, Math.PI * 2)
    ctx.fill()
    // Stem
    ctx.beginPath()
    ctx.moveTo(-h * 0.08, h * 0.1)
    ctx.quadraticCurveTo(-h * 0.1, h * 0.55, -h * 0.25, h * 0.88)
    ctx.lineTo(h * 0.25, h * 0.88)
    ctx.quadraticCurveTo(h * 0.1, h * 0.55, h * 0.08, h * 0.1)
    ctx.closePath()
    ctx.fill()
    ctx.restore()
}

function drawSuitSymbol(ctx: CanvasRenderingContext2D, suitType: string, cx: number, cy: number, size: number) {
    switch (suitType) {
        case 'H':
            drawHeart(ctx, cx, cy, size)
            break
        case 'D':
            drawDiamond(ctx, cx, cy, size)
            break
        case 'S':
            drawSpade(ctx, cx, cy, size)
            break
        case 'C':
            drawClub(ctx, cx, cy, size)
            break
    }
}

/** Font stack: Avenir Next for a clean geometric look on iOS, system fallbacks elsewhere */
export const CARD_FONT =
    '"Avenir Next", "Avenir", -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif'

function drawCardFace(
    card: Card,
    x: number,
    y: number,
    width: number,
    height: number,
    ctx: CanvasRenderingContext2D,
    isTopCard: boolean
) {
    const radius = getBorderRadius()
    const isRed = card.color === 'red'

    // 1. Card face background — theme-aware to avoid white-on-white with white card backs
    ctx.beginPath()
    ctx.roundRect(x, y, width, height, radius)
    ctx.fillStyle = appState.currentTheme.cardFaceColor
    ctx.fill()

    // 2. Heavier border for card edge definition (V3: much more visible than V2's 0.12 alpha)
    ctx.beginPath()
    ctx.roundRect(x, y, width, height, radius)
    ctx.lineWidth = Math.max(1.5, width * 0.03)
    ctx.strokeStyle = appState.currentTheme.cardBorderColor
    ctx.stroke()

    // Clip to card bounds so text doesn't bleed outside rounded corners
    ctx.save()
    ctx.beginPath()
    ctx.roundRect(x, y, width, height, radius)
    ctx.clip()

    // Ink color: red for ♥♦, near-black for ♠♣
    const inkColor = isRed ? appState.currentTheme.redCardColor : appState.currentTheme.blackCardColor

    // 3. Large centered suit symbol — only on top/fully-visible cards (V4/V5)
    //    Partially hidden cards in cascades only show the corner value.
    //    This prevents the suit from peeking out of the exposed sliver.
    //    V5: Canvas vector paths replace Unicode characters for pixel-perfect rendering.
    if (isTopCard) {
        const suitSize = Math.floor(height * 0.65)
        ctx.fillStyle = inkColor
        drawSuitSymbol(ctx, card.type, x + width * 0.5, y + height * 0.54, suitSize)
    }

    // 4. Corner: value text + small suit pip below it
    //    Position mirrors with handedness — always on the exposed cascade edge
    //    Right-handed: cascades left → right edge exposed → top-right corner
    //    Left-handed: cascades right → left edge exposed → top-left corner
    //    Text is centered in the exposed sliver (step/2 from the card edge)
    //    A mirrored (rotated 180°) rank+pip pair appears on the opposite bottom corner
    const leftCorner = isLeftHanded()
    const cascadeStep = tableauCardStep(false)
    const padX = cascadeStep / 2
    const padY = height * 0.06
    const cornerX = leftCorner ? x + padX : x + width - padX
    const oppositeCornerX = leftCorner ? x + width - padX : x + padX
    const cornerAlign: CanvasTextAlign = 'center'

    // Value text — heavy weight for legibility at small sizes
    const valueFontSize = Math.floor(height * 0.18)
    ctx.font = `800 ${valueFontSize}px ${CARD_FONT}`
    ctx.fillStyle = inkColor
    ctx.textAlign = cornerAlign
    ctx.textBaseline = 'top'
    ctx.fillText(card.value, cornerX, y + padY)

    // Suit pip + mirrored rank+pip — only on partially covered cards whose center suit is hidden
    if (!isTopCard) {
        const pipSize = Math.floor(valueFontSize * 1.0)
        const pipY = y + padY + valueFontSize + Math.floor(height * 0.04)
        drawSuitSymbol(ctx, card.type, cornerX, pipY + pipSize * 0.4, pipSize)

        // Mirrored rank + pip on opposite bottom corner (rotated 180°)
        ctx.save()
        ctx.translate(oppositeCornerX, y + height - padY)
        ctx.rotate(Math.PI)
        ctx.font = `800 ${valueFontSize}px ${CARD_FONT}`
        ctx.fillStyle = inkColor
        ctx.textAlign = cornerAlign
        ctx.textBaseline = 'top'
        ctx.fillText(card.value, 0, 0)
        drawSuitSymbol(ctx, card.type, 0, valueFontSize + Math.floor(height * 0.04) + pipSize * 0.4, pipSize)
        ctx.restore()
    }

    ctx.restore()
}

export function drawCard(card: Card, ctx: CanvasRenderingContext2D, isTopCard: boolean = true) {
    // Resolve the card's render position through the centralized position system.
    // This consults drag overlay, then animation overlay, then card.x/y (home position).
    const pos = getCardPosition(card)
    const { x, y } = pos

    if (card.isFaceDown) {
        drawCardBack(x, y, appState.cardWidth, appState.cardHeight, ctx)
    } else {
        drawCardFace(card, x, y, appState.cardWidth, appState.cardHeight, ctx, isTopCard)
    }
}

/** Draw a card at an explicit position — bypasses the position overlay system.
 *  Used by the win animation to render cards at arbitrary cascade positions. */
export function drawCardAt(card: Card, ctx: CanvasRenderingContext2D, x: number, y: number) {
    if (card.isFaceDown) {
        drawCardBack(x, y, appState.cardWidth, appState.cardHeight, ctx)
    } else {
        drawCardFace(card, x, y, appState.cardWidth, appState.cardHeight, ctx, true)
    }
}

function drawText(text, startX, startY, fontSize: number, ctx) {
    ctx.save()

    ctx.fillStyle = appState.currentTheme.textColor

    // Create responsive font size that scales with screen but has reasonable limits
    // Use min() to cap the maximum size, and scale based on smaller dimension for consistency
    const minDimension = Math.min(appState.windowWidth, appState.windowHeight)
    const scaledFontSize = Math.min(
        Math.floor((fontSize * minDimension) / 100), // Scale based on smaller dimension
        32 // Cap maximum font size at 32px
    )

    ctx.font = `${scaledFontSize}px Avenir Book`
    // No shadowBlur — expensive on iOS Safari. Use thin stroke for text contrast if needed.
    ctx.fillText(text, startX, startY)
    ctx.restore()
}
